/**
 * ll87CycleStates — the LL87 10-state eligibility cycle machine (PRD
 * FR-11), as a standalone, dependency-free module.
 *
 * This is the owner-facing "truth layer" the resolver stamps on every
 * LL87 result (`cycle_state` on the building-codes payload). The FE
 * status/enrichment helpers key off it exhaustively via
 * `satisfies Record<Ll87CycleState, …>` — adding a state without a
 * decision fails the build at the consumer.
 *
 * Why this lives here and not in the LL87 Flow redesign's
 * `Flow/adapterTypes.ts`: the applicable-codes row enrichments
 * (`components/portal/ll87Status.ts` — cycle eyebrow, statutory-deadline
 * subline, lead-time nudge) need ONLY the cycle-state union + guard, not
 * the Flow dispatcher, the 14-status wizard machine, or the pending
 * NEW-BE contract stubs. Isolating the union here keeps the Portal
 * applicable-codes surface free of any Flow/ dependency. Keep the state
 * set in lockstep with the backend resolver
 * (`backend/server/tracking/services/ll87_eligibility.py`).
 *
 * These are state VALUES — opaque tokens the FE maps, never keys it
 * parses. The axios interceptor camelizes KEYS only, so `cycle_state`
 * arrives as-authored.
 */

// ---------------------------------------------------------------------
// The 10-state eligibility cycle machine (PRD FR-11) — the owner-facing
// truth layer.
// ---------------------------------------------------------------------

export const LL87_CYCLE_STATES = [
  "covered_upcoming",
  "covered_due_this_year",
  "covered_cycle_unresolved",
  "accruing_missed_cycle",
  "extension_pending",
  "deferred",
  "filed_awaiting_acceptance",
  "accepted",
  "not_covered",
  "unknown",
] as const;

export type Ll87CycleState = (typeof LL87_CYCLE_STATES)[number];

export const isLl87CycleState = (v: unknown): v is Ll87CycleState =>
  typeof v === "string" && (LL87_CYCLE_STATES as readonly string[]).includes(v);
